// ============================================================
// TMR Portal Admin — how a contract's status reads on screen
// ============================================================
import type { ContractRecord, ContractSignerRole, ContractStatus } from './contract';

/**
 * The badge key for each contract status.
 *
 * The keys are **prefixed** rather than the backend's own values, and that indirection is load-bearing: the
 * shared `STATUS_MAP` already uses `Pending` for a stale legacy copy *and* for a waiting access request, so a
 * bare `Pending` here would make a contract awaiting its first send read as a replication problem. `StatusBadge`
 * falls back silently on an unknown key, which turns any collision into a status quietly showing as another
 * one. Same reasoning as `BUSINESS_STATUS_BADGE`.
 */
export const CONTRACT_STATUS_BADGE: Record<ContractStatus, string> = {
  Pending: 'contrato_pendente',
  AwaitingSignature: 'contrato_aguardando',
  Signed: 'contrato_assinado',
  Cancelled: 'contrato_cancelado',
  Expired: 'contrato_expirado',
  Refused: 'contrato_recusado',
};

/** Contract statuses in lifecycle order, with the label the filter tabs and the panel show. */
export const CONTRACT_STATUS_OPTIONS: { value: ContractStatus; label: string }[] = [
  { value: 'Pending', label: 'Pendente' },
  { value: 'AwaitingSignature', label: 'Aguardando assinatura' },
  { value: 'Signed', label: 'Assinado' },
  { value: 'Refused', label: 'Recusado' },
  { value: 'Expired', label: 'Expirado' },
  { value: 'Cancelled', label: 'Cancelado' },
];

/** The statuses a contract never leaves. Offering any action on one of these is a guaranteed 409. */
const CLOSED_STATUSES: ReadonlySet<ContractStatus> = new Set<ContractStatus>([
  'Signed',
  'Cancelled',
  'Expired',
  'Refused',
]);

/**
 * Whether the contract has reached a state it will not leave.
 *
 * @param status The contract's status.
 */
export function isClosed(status: ContractStatus): boolean {
  return CLOSED_STATUSES.has(status);
}

/**
 * Whether this contract can be sent for signature.
 *
 * Mirrors `RequestSignatureHandler`'s guard exactly — `Pending` and nothing else — so the button appears
 * precisely where the server accepts it. Note what this is *not*: "has no `signatureExternalId`". A `Refused`
 * contract has a document key at the provider and still cannot be re-sent; asking would answer 409.
 *
 * @param status The contract's status.
 */
export function canRequestSignature(status: ContractStatus): boolean {
  return status === 'Pending';
}

/**
 * Whether this contract can be discarded from the portal.
 *
 * `Pending` and nothing else, mirroring `CancelContractHandler`. The interesting exclusion is
 * `AwaitingSignature`: the document is already in somebody's inbox with a live signing link, so cancelling
 * it belongs at the provider — offering the action here would promise something the server refuses, and
 * worse, would suggest the link stops working.
 *
 * @param status The contract's status.
 */
export function canCancel(status: ContractStatus): boolean {
  return status === 'Pending';
}

/**
 * Whether this contract's document can still be acted on at the provider — its request resent, its
 * signatory replaced with a corrected contact, or its signing link read.
 *
 * `AwaitingSignature` and nothing else, mirroring the backend's single guard (`OutForSignature`). A
 * `Refused` or `Expired` contract has a document too, but a closed one: it cannot be re-sent or re-pointed,
 * and needs a new contract. The provider can still refuse one of these (the representative signed a
 * moment ago), which the action reports as a 409 in place.
 *
 * @param status The contract's status.
 */
export function isOutForSignature(status: ContractStatus): boolean {
  return status === 'AwaitingSignature';
}

/**
 * Where the browser downloads a contract's file from — our own API, never the provider.
 *
 * Same-origin on purpose, and it is the fix for a real defect: the panel used to link straight to the
 * provider's signed URL, and a browser navigating to a `.docx` hands that URL to the Office online viewer,
 * which cannot fetch it and shows a 404 of its own. Going through our API means the response can be an
 * attachment — the browser saves it instead of trying to open it — and the provider's short-lived link is
 * minted at the moment of the click rather than when the panel opened.
 *
 * @param contractId The contract whose file to download.
 * @param variant The signed file, or the document as it was raised.
 */
export function contractFileUrl(contractId: string, variant: 'signed' | 'original'): string {
  return `/api/v1/contracts/${contractId}/document/file?variant=${variant}`;
}

/**
 * The document's name as a person reads it — the file name out of the provider's full path, without the
 * extension.
 *
 * The record keeps the path verbatim (`/Contratos de Empresa/Acme Ltda-12345678000199.docx`) because that
 * is the provider's own string; turning it into a name is a screen's job, and this is where it happens.
 *
 * @param documentPath The provider's path, or null for a contract that was never sent.
 */
export function documentDisplayName(documentPath: string | null): string | null {
  if (!documentPath) return null;

  const fileName = documentPath.split('/').pop()?.trim();
  if (!fileName) return null;

  return fileName.replace(/\.[^.]+$/, '') || null;
}

/**
 * How a contract is titled in a list: the document's name at the provider, falling back to the date it was
 * issued while there is no document.
 *
 * The fallback matters because a contract exists before its document does — it is drawn up `Pending` and
 * only named when it is sent. What the title must **never** fall back to is the signature stage: that was
 * the first attempt, and it put the word "Encerrado" where a name belongs, so a signed contract read as if
 * something had gone wrong with it.
 *
 * @param contract The contract to title.
 */
export function contractTitle(contract: Pick<ContractRecord, 'createdAt' | 'documentPath'>): string {
  const name = documentDisplayName(contract.documentPath);
  if (name) return name;

  const issued = new Date(contract.createdAt).toLocaleDateString('pt-BR');
  return `Contrato de ${issued}`;
}

/**
 * What each provider event is called on screen.
 *
 * The backend passes the provider's raw event name through and stores no UI text (07 §5), so the wording
 * lives here. Every entry is a sentence about the **document**, in the same shape — subject then past
 * participle. That uniformity is the point: the list used to mix "Documento criado" with a bare "Assinado"
 * and with raw provider names like `signature_started`, and a timeline that changes register three times
 * reads as three different systems talking.
 */
const DOCUMENT_EVENT_LABELS: Record<string, string> = {
  upload: 'Documento criado',
  add_signer: 'Signatário adicionado',
  remove_signer: 'Signatário removido',
  update_deadline: 'Prazo alterado',
  view: 'Documento visualizado',
  signature_started: 'Assinatura iniciada',
  sign: 'Documento assinado',
  refusal: 'Assinatura recusada',
  cancel: 'Documento cancelado',
  deadline: 'Prazo expirado',
  auto_close: 'Documento concluído',
  close: 'Documento concluído',
  document_closed: 'Documento concluído',
};

/** How each side of the contract is named when a signature is attributed to it. */
const SIGNER_ROLE_LABELS: Record<ContractSignerRole, string> = {
  Party: 'pela empresa',
  Tmr: 'pela TMR',
};

/**
 * The sentence shown for one provider event.
 *
 * An unknown name is **named**, not dropped and not printed bare: the provider adds events on its own
 * schedule, so a timeline that hides what it does not recognize is worse than one showing an unfamiliar
 * word — but the raw word alone, sitting among finished sentences, reads like a bug. Saying it came from
 * the provider is both honest and in register.
 *
 * @param name The provider's raw event name.
 * @param actorRole Which side signed, for a signature event; the label says so when the provider attributes it.
 */
export function documentEventLabel(name: string, actorRole?: ContractSignerRole | null): string {
  const label = DOCUMENT_EVENT_LABELS[name];
  if (!label) return `Evento do provedor: ${name}`;

  // Only a signature has sides. Appending the actor to anything else would read as if the Empresa had
  // created the document or set its deadline.
  const role = name === 'sign' && actorRole ? SIGNER_ROLE_LABELS[actorRole] : null;
  return role ? `${label} ${role}` : label;
}

/** Provider labels for the ones the backend knows about (02 §7). */
const PROVIDER_LABELS: Record<string, string> = {
  ClickSign: 'ClickSign',
  DocuSeal: 'DocuSeal',
  Autentique: 'Autentique',
  Internal: 'Interno TMR',
};

/**
 * How a signature provider is named on screen, falling back to the raw key.
 *
 * The fallback is the point: a provider the backend starts answering with shows up under its own name rather
 * than disappearing behind an empty label.
 *
 * @param provider The `signatureProvider` value from the contract.
 */
export function signatureProviderLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

/**
 * Where the contract stands in getting signed — the fact the operator actually asks for.
 *
 * Reads `status`, `partySignedAt` and `tmrSignedAt` **together**, because no single field answers it.
 * `AwaitingSignature` covers two different situations: nobody has signed yet, and the Empresa has signed
 * while the TMR has not. Both would show the same "Aguardando assinatura" badge, hiding which side is
 * holding things up.
 *
 * Either side may sign first: the TMR enters as `contractee` and is notified like any other signatory
 * (02 §11, decision of 2026-07-29) — it does not auto-sign. So a contract with `tmrSignedAt` set and
 * `partySignedAt` still null is waiting on the **party**, not on us.
 *
 * @param contract The contract to read.
 */
export function signatureStage(
  contract: Pick<ContractRecord, 'status' | 'partySignedAt' | 'tmrSignedAt'>,
): SignatureStage {
  // `Signed` is told apart from the other three terminals on purpose. They are all "the contract will not
  // move again", but only one of them is a good outcome, and a single label for the four would have to
  // either call a refused contract "Concluído" or call a fully signed one "Encerrado".
  if (contract.status === 'Signed') return 'signed';
  if (isClosed(contract.status)) return 'closed';
  if (contract.status === 'Pending') return 'not_sent';
  return contract.partySignedAt ? 'awaiting_tmr' : 'awaiting_party';
}

/** Where a contract stands in getting signed, as the screen groups it. */
export type SignatureStage = 'not_sent' | 'awaiting_party' | 'awaiting_tmr' | 'signed' | 'closed';

/** What each stage is called on screen. */
export const SIGNATURE_STAGE_LABEL: Record<SignatureStage, string> = {
  not_sent: 'Não enviado para assinatura',
  awaiting_party: 'Aguardando a assinatura da empresa',
  awaiting_tmr: 'Empresa assinou — aguardando a TMR',
  signed: 'Concluído',
  // Cancelled, Expired and Refused: the contract is over without having been signed.
  closed: 'Encerrado',
};
